import classNames from 'classnames';
import { X as CloseIcon } from 'phosphor-react';
import type * as React from 'react';
import { type PropsWithChildren, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { NETWORK_ERROR_MESSAGE } from '../constants';
import type { FlowActions } from '../hooks/useFlowActions';
import { getComponent } from '../services/components';
import { join, ping } from '../services/network';
import { type AppState, PageType } from '../types/models';
import type {
    HeaderComponentProps,
    NavigationProps,
    OutcomeProps,
    PageElementProps,
    ProgressTrackerProps,
} from '../types/public/component-props';
import type { ExecutionMode, Outcome } from '../types/public/models';
import type { Options } from '../types/public/options';
import { createMarkup } from '../utils/core';
import { parseRootFaultMessages, reportError } from '../utils/errors';
import Authentication from './Authentication';
import ErrorBoundary from './ErrorBoundary';
import ErrorNotification from './ErrorNotification';
import HistoricalNavigation from './HistoricalNavigation';
import Loader from './Loader';
import Wait from './Wait';

export type SharedRootProps = PropsWithChildren<{
    options?: Partial<Options>;
    isModal?: boolean;
    isModalOpen?: boolean;
}>;

export type RootProps = PropsWithChildren<{
    tenantId: string;
    flowId?: string | null;
    stateId?: string | null;
    flowVersion?: string | null;
    authenticationToken?: string | null;
    options?: Partial<Options>;
    executionMode?: ExecutionMode;
    environmentId?: string | null;
    isModal?: boolean;
    appState: AppState;
    isModalOpen?: boolean;
    actions: FlowActions;
}>;

const Root = ({ isModal = false, appState, isModalOpen, options = {}, actions }: RootProps) => {
    const { headerImageUrl, headerTitle, navigation: navigationOptions } = options;
    console.debug('Render Root');

    const { execute, updateElement, updateObjectData, selectItems, setErrorMessage, setAppState } =
        actions;

    const [backOutcomeId, setBackOutcomeId] = useState<string | null>(null);

    const onDialogCancel = useCallback(
        (event: Event | React.MouseEvent<HTMLElement>) => {
            // Prevent browser from closing the modal and leaving us on the base page
            event.preventDefault();
            if (backOutcomeId) {
                execute({ outcomeId: backOutcomeId });
            }
        },
        [backOutcomeId, execute],
    );

    const dialogRef: React.RefObject<HTMLDialogElement> = useRef(null);

    const filterAndSortBackOutcomes = (outcomesToFilter: Outcome[]) => {
        const backOutcomes: Outcome[] = outcomesToFilter.filter(
            (outcome: Outcome) => outcome.pageActionType === 'Back',
        );

        backOutcomes.sort((firstOutcome: Outcome, secondOutcome: Outcome) =>
            firstOutcome.order > secondOutcome.order ? 1 : -1,
        );

        if (backOutcomes.length > 0) {
            setBackOutcomeId(backOutcomes[0].id);
        } else {
            setBackOutcomeId(null);
        }
    };

    const pushStateFunc = useCallback(
        (backOutcomeId: string | null) => {
            const title: unknown = null;
            history.pushState(null, title as string, document.URL);

            if (backOutcomeId) {
                execute({ outcomeId: backOutcomeId });
            }
        },
        [execute],
    );

    useEffect(() => {
        const dialog = dialogRef?.current;
        if (isModal) {
            if (!dialog?.open) {
                dialog?.showModal();
            }
            dialog?.addEventListener('cancel', onDialogCancel);
        }
        return () => {
            dialog?.removeEventListener('cancel', onDialogCancel);
        };
    }, [isModal, onDialogCancel]);

    // Disable the back button.
    useEffect(() => {
        if (!appState.options.allowLocationControl) {
            return;
        }

        // If there is a back outcome then it should be executed.
        const pushState = () => pushStateFunc(backOutcomeId);

        pushStateFunc(null);
        if (!(isModalOpen || isModal) || (isModalOpen && isModal)) {
            window.addEventListener('popstate', pushState);
        }

        return () => {
            window.removeEventListener('popstate', pushState);
        };
    }, [backOutcomeId, isModal, pushStateFunc, isModalOpen, appState.options.allowLocationControl]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        filterAndSortBackOutcomes(appState.outcomes ?? []);
    }, [appState.outcomes]);

    useEffect(() => {
        if (appState.rootFaults) {
            reportError(appState.rootFaults);
        }

        if (appState.rootFaults && appState.displayRootFaults) {
            setErrorMessage(parseRootFaultMessages(appState.rootFaults));
        }
    }, [appState.rootFaults, appState.displayRootFaults, setErrorMessage]);

    if (appState.statusCode === '') {
        return <ErrorNotification errorMessage={appState.errorMessage} />;
    }

    const {
        outcomes,
        rootElement,
        authenticationToken,
        authorizationContext,
        stateId,
        statusCode,
        tenantId,
        hasSubmitted,
        notAuthorizedMessage,
        waitExpiresAt,
        title,
        isPageWait,
    } = appState;

    const onJoin = async () => {
        try {
            const newState = await join(appState);
            setAppState(newState);
        } catch (_ex) {
            setErrorMessage(NETWORK_ERROR_MESSAGE);
        }
    };

    const onPing = async () => {
        try {
            return await ping(appState.tenantId, appState.stateId, appState.stateToken);
        } catch (_ex) {
            setErrorMessage(NETWORK_ERROR_MESSAGE);
            return false;
        }
    };

    const onLogin = async (authenticationToken: string) => {
        try {
            const newState = await join({ ...appState, authenticationToken });
            setAppState(newState);
        } catch (_ex) {
            setErrorMessage(NETWORK_ERROR_MESSAGE);
        }
    };

    const Header = getComponent('header') as React.ComponentType<HeaderComponentProps>;
    const Outcome = getComponent('outcome') as React.ComponentType<OutcomeProps>;
    const Navigation = getComponent('navigation') as React.ComponentType<NavigationProps>;
    const ProgressTracker = getComponent(
        'progress-tracker',
    ) as React.ComponentType<ProgressTrackerProps>;

    const Container = getComponent(
        appState.rootElement?.type ?? 'vertical_flow',
    ) as React.ComponentType<PageElementProps>;
    const mainContainer = rootElement ? (
        <Container
            currentMapElementId={appState.currentMapElementId}
            authenticationToken={authenticationToken}
            tenantId={tenantId}
            stateId={stateId}
            element={rootElement}
            hasSubmitted={hasSubmitted}
            execute={execute}
            outcomes={outcomes}
            featureFlags={appState.featureFlags}
            updateElement={updateElement}
            updateObjectData={updateObjectData}
            selectItems={selectItems}
            setErrorMessage={setErrorMessage}
        />
    ) : null;

    const navigations = appState.navigations
        ? appState.navigations.filter((nav) => !nav.progressTracker)
        : null;
    const progressTrackers = appState.navigations
        ? appState.navigations.filter((nav) => nav.progressTracker)
        : null;

    const hasNavigation = navigations && navigations.length > 0;

    const hasWait = appState.invokeType === 'WAIT' || appState.isPageWait;

    const pageOutcomes = outcomes?.filter((outcome) => outcome.pageObjectBindingId === null);

    const contentClasses = classNames({
        content: true,
        'has-error-notification': appState.errorMessage !== null,
    });

    const root = (
        <>
            {!isModal && (hasNavigation || headerImageUrl || headerTitle) && (
                <Suspense fallback={<div />} key="header">
                    <Header
                        headerImageUrl={headerImageUrl || ''}
                        headerTitle={headerTitle}
                        isFixed={navigations !== null && (navigationOptions?.isFixed ?? false)}
                    >
                        {hasNavigation && (
                            <Navigation
                                navigation={navigations[0]}
                                execute={execute}
                                flowId={appState.flowId}
                                tenantId={appState.tenantId}
                            />
                        )}
                    </Header>
                </Suspense>
            )}
            <section className={contentClasses}>
                <Authentication
                    authorizationContext={authorizationContext}
                    authenticationToken={authenticationToken}
                    statusCode={statusCode}
                    invokeType={appState.invokeType}
                    notAuthorizedMessage={notAuthorizedMessage}
                    tenantId={tenantId}
                    stateId={stateId}
                    onLogin={onLogin}
                >
                    {appState.isHistoricalNavigationEnabled && (
                        <HistoricalNavigation
                            tenantId={appState.tenantId}
                            stateId={appState.stateId}
                            stateToken={appState.stateToken}
                            currentMapElementName={appState.currentMapElementName}
                            execute={execute}
                        />
                    )}
                    {progressTrackers?.map((navigation) => (
                        <ProgressTracker navigation={navigation} key={navigation.id} />
                    ))}
                    {rootElement && (
                        <Suspense fallback={<div />} key={rootElement.id}>
                            <ErrorBoundary>
                                {isModal ? (
                                    <>
                                        <span className="modal-title">
                                            {title}
                                            {backOutcomeId ? (
                                                <button
                                                    type="button"
                                                    className="modal-close"
                                                    aria-label="Close"
                                                    onClick={onDialogCancel}
                                                >
                                                    <CloseIcon width={16} height={16} />
                                                </button>
                                            ) : null}
                                        </span>
                                        <div className="modal-content">{mainContainer}</div>
                                    </>
                                ) : (
                                    mainContainer
                                )}
                            </ErrorBoundary>
                        </Suspense>
                    )}

                    {pageOutcomes && pageOutcomes.length > 0 && (
                        <div className={`outcomes ${isModal ? 'modal-footer' : ''}`}>
                            {pageOutcomes.map((outcome) => (
                                <Suspense fallback={<div />} key={outcome.id}>
                                    <Outcome outcome={outcome} execute={execute} />
                                </Suspense>
                            ))}
                        </div>
                    )}
                </Authentication>

                {hasWait && appState.errorMessage === null && (
                    <Wait
                        waitExpiresAt={waitExpiresAt}
                        join={onJoin}
                        ping={onPing}
                        isPageWait={isPageWait}
                    >
                        <div
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: Unsure of alternative
                            dangerouslySetInnerHTML={createMarkup(
                                appState.waitMessage ?? 'Waiting',
                            )}
                        />
                    </Wait>
                )}
                <ErrorNotification errorMessage={appState.errorMessage} />
            </section>
        </>
    );

    if (appState.pageType === PageType.Modal) {
        return (
            <dialog ref={dialogRef}>
                {root}
                <Loader isLoading={appState.isLoading} />
            </dialog>
        );
    }
    return root;
};

export default Root;
